import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../utils/api';
import { 
  UserPlus, Search, QrCode, Download, Edit2, Power, X, Trash2,
  Stethoscope, Phone, Mail, MapPin, Percent, Building,
  Copy, ExternalLink, Users, LogIn, UserCheck, FileText, Upload
} from 'lucide-react';

const API_URL = getApiUrl();

const DoctorsPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [deletingDoctor, setDeletingDoctor] = useState(null);

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/doctors`, { withCredentials: true });
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  };

  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = doctor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doctor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doctor.clinic_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || doctor.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleToggleStatus = async (doctor) => {
    const newStatus = doctor.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.patch(
        `${API_URL}/api/doctors/${doctor.doctor_id}/status?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Doctor ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchDoctors();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteDoctor = async (doctor) => {
    setDeletingDoctor(doctor);
  };

  const confirmDeleteDoctor = async () => {
    if (!deletingDoctor) return;
    try {
      await axios.delete(`${API_URL}/api/doctors/${deletingDoctor.doctor_id}`, { withCredentials: true });
      toast.success('Doctor removed successfully');
      setDeletingDoctor(null);
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove doctor');
      setDeletingDoctor(null);
    }
  };

  const handleViewQR = async (doctor) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/doctors/${doctor.doctor_id}/qr-code`,
        { withCredentials: true }
      );
      setShowQRModal(response.data);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const downloadQR = (qrData) => {
    const link = document.createElement('a');
    link.download = `QR_${qrData.referral_code}.png`;
    link.href = qrData.qr_code_base64;
    link.click();
  };

  const handleCreateUserAccount = async (doctor) => {
    try {
      await axios.post(
        `${API_URL}/api/doctors/${doctor.doctor_id}/create-user-account`,
        {},
        { withCredentials: true }
      );
      toast.success('User account created for doctor');
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user account');
    }
  };

  const handleLoginAsDoctor = async (doctor) => {
    if (!doctor.user_id) {
      // First create user account
      try {
        await axios.post(
          `${API_URL}/api/doctors/${doctor.doctor_id}/create-user-account`,
          {},
          { withCredentials: true }
        );
        toast.success('User account created');
        // Refresh doctor list to get user_id
        const response = await axios.get(`${API_URL}/api/doctors`, { withCredentials: true });
        const updatedDoctor = response.data.find(d => d.doctor_id === doctor.doctor_id);
        if (updatedDoctor) {
          doctor = updatedDoctor;
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create user account');
        return;
      }
    }

    setImpersonating(doctor.doctor_id);
    try {
      const response = await axios.post(
        `${API_URL}/api/doctors/${doctor.doctor_id}/impersonate`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Switching to ${doctor.name}'s account...`);
      
      // Force a complete page reload to get fresh cookies
      // Using replace to prevent back button issues
      window.location.replace('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as doctor');
      setImpersonating(null);
    }
  };

  return (
    <div data-testid="doctors-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Doctor Partners</h1>
          <p className="text-slate-500">Manage doctor partnerships and referral programs</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-doctor-btn"
        >
          <UserPlus className="w-4 h-4" />
          Add Doctor
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{doctors.length}</p>
              <p className="text-sm text-slate-500">Total Partners</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {doctors.filter(d => d.status === 'active').length}
              </p>
              <p className="text-sm text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <QrCode className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{doctors.length}</p>
              <p className="text-sm text-slate-500">QR Codes</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
              <Percent className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {doctors.length > 0 ? Math.round(doctors.reduce((a, d) => a + (d.commission_percentage || 10), 0) / doctors.length) : 0}%
              </p>
              <p className="text-sm text-slate-500">Avg Commission</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, email or clinic..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-base pl-10"
            data-testid="search-doctors"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-base w-full sm:w-40"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="table-wrapper overflow-x-auto">
        <table className="w-full">
          <thead className="table-header">
            <tr>
              <th className="p-4 text-left">Doctor</th>
              <th className="p-4 text-left">Clinic</th>
              <th className="p-4 text-left">Contact</th>
              <th className="p-4 text-left">Referral Code</th>
              <th className="p-4 text-left">Commission</th>
              <th className="p-4 text-left">Status</th>
              <th className="p-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="p-8 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                </td>
              </tr>
            ) : filteredDoctors.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-slate-500">
                  No doctors found
                </td>
              </tr>
            ) : (
              filteredDoctors.map((doctor) => (
                <tr key={doctor.doctor_id} className="table-row" data-testid={`doctor-row-${doctor.doctor_id}`}>
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <Stethoscope className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{doctor.name}</div>
                        <div className="text-xs text-slate-500">{doctor.specialization || 'Gynecologist'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="text-slate-900">{doctor.clinic_name || '-'}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[200px]">{doctor.clinic_address || ''}</div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1 text-sm">
                      <Phone className="w-3 h-3 text-slate-400" />
                      <span>{doctor.phone}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Mail className="w-3 h-3" />
                      <span>{doctor.email}</span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        {doctor.referral_code}
                      </span>
                      <button
                        onClick={() => copyToClipboard(doctor.referral_code)}
                        className="p-1 text-slate-400 hover:text-slate-600"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className="font-semibold text-slate-900">
                      {doctor.commission_type === 'slab' && doctor.commission_slabs?.length > 0 
                        ? `${doctor.commission_slabs[0]?.commission_percentage || 10}% - ${doctor.commission_slabs[doctor.commission_slabs.length - 1]?.commission_percentage || 10}%`
                        : `${doctor.commission_percentage || 10}%`
                      }
                    </span>
                    <div className="text-xs text-slate-500">
                      {doctor.commission_type === 'slab' ? `${doctor.commission_slabs?.length || 0} slabs` : 'flat rate'}
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      doctor.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'
                    }`}>
                      {doctor.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleViewQR(doctor)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md"
                        title="View QR Code"
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingDoctor(doctor)}
                        className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-md"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(doctor)}
                        className={`p-1.5 rounded-md ${
                          doctor.status === 'active' 
                            ? 'text-red-600 hover:bg-red-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={doctor.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      {doctor.status === 'inactive' && (
                        <button
                          onClick={() => handleDeleteDoctor(doctor)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"
                          title="Remove Doctor"
                          data-testid={`delete-doctor-${doctor.doctor_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingDoctor) && (
        <DoctorFormModal
          doctor={editingDoctor}
          allDoctors={doctors}
          onClose={() => {
            setShowAddModal(false);
            setEditingDoctor(null);
          }}
          onSave={() => {
            setShowAddModal(false);
            setEditingDoctor(null);
            fetchDoctors();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingDoctor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Remove Doctor</h3>
              <button onClick={() => setDeletingDoctor(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-slate-600">
                Are you sure you want to remove <span className="font-semibold">{deletingDoctor.name}</span>? This will also delete their user account if one exists. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeletingDoctor(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={confirmDeleteDoctor} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700" data-testid="confirm-delete-doctor-btn">Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <QRCodeModal
          data={showQRModal}
          onClose={() => setShowQRModal(null)}
          onDownload={() => downloadQR(showQRModal)}
        />
      )}
    </div>
  );
};

// Doctor Form Modal
const DoctorFormModal = ({ doctor, allDoctors = [], onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: doctor?.name || '',
    email: doctor?.email || '',
    phone: doctor?.phone || '',
    clinic_name: doctor?.clinic_name || '',
    clinic_address: doctor?.clinic_address || '',
    specialization: doctor?.specialization || 'Gynecologist',
    degree: doctor?.degree || '',
    years_experience: doctor?.years_experience || '',
    image_url: doctor?.image_url || '',
    consultation_fee: doctor?.consultation_fee || 0,
    consultation_active: doctor?.consultation_active || false,
    consultation_slot_duration: doctor?.consultation_slot_duration || 30,
    consultation_advance_days: doctor?.consultation_advance_days || 7,
    consultation_min_notice_hours: doctor?.consultation_min_notice_hours || 4,
    consultation_location_types: doctor?.consultation_location_types || ['online', 'offline'],
    commission_type: doctor?.commission_type || 'percentage',
    commission_percentage: doctor?.commission_percentage || 10,
    commission_slabs: doctor?.commission_slabs || [],
    partnership_manager_name: doctor?.partnership_manager_name || '',
    partnership_manager_phone: doctor?.partnership_manager_phone || '',
    partnership_manager_email: doctor?.partnership_manager_email || '',
    referred_by_doctor_id: doctor?.referred_by_doctor_id || '',
    agreement_signed: doctor?.agreement_signed || false
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [agreementFile, setAgreementFile] = useState(null);
  const [agreementUrl, setAgreementUrl] = useState(doctor?.agreement_file_url || '');

  const addSlab = () => {
    const lastSlab = formData.commission_slabs[formData.commission_slabs.length - 1];
    const nextMin = lastSlab ? (lastSlab.max_referrals || 0) + 1 : 0;
    setFormData({
      ...formData,
      commission_slabs: [
        ...formData.commission_slabs,
        { min_referrals: nextMin, max_referrals: nextMin + 5, commission_percentage: 10 }
      ]
    });
  };

  const updateSlab = (index, field, value) => {
    const newSlabs = [...formData.commission_slabs];
    newSlabs[index] = { ...newSlabs[index], [field]: value };
    setFormData({ ...formData, commission_slabs: newSlabs });
  };

  const removeSlab = (index) => {
    setFormData({
      ...formData,
      commission_slabs: formData.commission_slabs.filter((_, i) => i !== index)
    });
  };

  const handleAgreementUpload = async (file) => {
    if (!file) return;
    
    setUploadingAgreement(true);
    const fileFormData = new FormData();
    fileFormData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/upload/agreement`,
        fileFormData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      setAgreementUrl(response.data.file_url);
      setAgreementFile(file);
      toast.success('Agreement uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload agreement');
    } finally {
      setUploadingAgreement(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const submitData = {
      ...formData,
      agreement_file_url: agreementUrl || doctor?.agreement_file_url || null,
      agreement_signed: !!agreementUrl || formData.agreement_signed
    };
    
    try {
      if (doctor) {
        await axios.put(
          `${API_URL}/api/doctors/${doctor.doctor_id}`,
          submitData,
          { withCredentials: true }
        );
        toast.success('Doctor updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/doctors`,
          submitData,
          { withCredentials: true }
        );
        toast.success('Doctor added successfully');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save doctor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            {doctor ? 'Edit Doctor Partner' : 'Add New Doctor Partner'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Stethoscope className="w-4 h-4" />
              Doctor Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-base"
                  placeholder="Dr. Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Specialization</label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  className="input-base"
                  placeholder="Gynecologist"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-base"
                  placeholder="doctor@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-base"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
          </div>

          {/* Clinic Info */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Building className="w-4 h-4" />
              Clinic &amp; Profile
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Clinic Name</label>
                <input type="text" value={formData.clinic_name}
                  onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })}
                  className="input-base" placeholder="Clinic/Hospital Name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Clinic Address</label>
                <input type="text" value={formData.clinic_address}
                  onChange={(e) => setFormData({ ...formData, clinic_address: e.target.value })}
                  className="input-base" placeholder="Full address" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Degree / Qualification</label>
                <input type="text" value={formData.degree}
                  onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                  className="input-base" placeholder="MBBS, MD (OBG)" data-testid="doctor-degree" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Years of Experience</label>
                <input type="number" min="0" value={formData.years_experience}
                  onChange={(e) => setFormData({ ...formData, years_experience: e.target.value ? parseInt(e.target.value) : '' })}
                  className="input-base" placeholder="15" data-testid="doctor-experience" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Profile Image</label>
                <DoctorImageUpload
                  currentUrl={formData.image_url}
                  onUpload={(url) => setFormData({ ...formData, image_url: url })}
                />
                <p className="text-xs text-slate-400 mt-1">Visible to patients on the consultation booking page</p>
              </div>
            </div>
          </div>

          {/* Consultation Settings */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Stethoscope className="w-4 h-4" />
              Consultation Settings
            </h4>
            <div className="bg-slate-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={formData.consultation_active}
                    onChange={(e) => setFormData({ ...formData, consultation_active: e.target.checked })} />
                  <div className="w-9 h-5 bg-slate-300 rounded-full peer peer-checked:bg-emerald-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                </label>
                <span className="text-sm font-medium text-slate-700">Enable paid consultations</span>
              </div>
              {formData.consultation_active && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Consultation Fee (₹)</label>
                    <input type="number" min="0" step="50" value={formData.consultation_fee}
                      onChange={(e) => setFormData({ ...formData, consultation_fee: parseFloat(e.target.value) || 0 })}
                      className="input-base" placeholder="500" data-testid="doctor-consult-fee" />
                    <p className="text-xs text-slate-400 mt-1">Patient pays this + 18% GST</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Slot Duration (min)</label>
                    <select value={formData.consultation_slot_duration}
                      onChange={(e) => setFormData({ ...formData, consultation_slot_duration: parseInt(e.target.value) })}
                      className="input-base">
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Advance Booking (days)</label>
                    <input type="number" min="1" max="30" value={formData.consultation_advance_days}
                      onChange={(e) => setFormData({ ...formData, consultation_advance_days: parseInt(e.target.value) || 7 })}
                      className="input-base" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Doctor Referral */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Doctor Referral (Optional)
            </h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Referred By Doctor</label>
              <select
                value={formData.referred_by_doctor_id}
                onChange={(e) => setFormData({ ...formData, referred_by_doctor_id: e.target.value })}
                className="input-base"
              >
                <option value="">-- None (Direct Partnership) --</option>
                {allDoctors
                  .filter(d => d.doctor_id !== doctor?.doctor_id && d.status === 'active')
                  .map(d => (
                    <option key={d.doctor_id} value={d.doctor_id}>
                      Dr. {d.name} ({d.clinic_name || 'No Clinic'})
                    </option>
                  ))
                }
              </select>
              <p className="text-xs text-slate-500 mt-1">
                If this doctor was referred by another doctor, select them here to enable referral bonus.
              </p>
            </div>
          </div>

          {/* Commission */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Percent className="w-4 h-4" />
              Commission Structure
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Commission Type</label>
                <select
                  value={formData.commission_type}
                  onChange={(e) => setFormData({ ...formData, commission_type: e.target.value })}
                  className="input-base"
                >
                  <option value="percentage">Flat Percentage</option>
                  <option value="slab">Slab-based (Tiered)</option>
                </select>
              </div>
              {formData.commission_type === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Commission Percentage</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={formData.commission_percentage}
                      onChange={(e) => setFormData({ ...formData, commission_percentage: parseFloat(e.target.value) || 0 })}
                      className="input-base pr-8"
                      placeholder="10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Slab Configuration */}
            {formData.commission_type === 'slab' && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-600">Configure commission slabs based on number of enrollments</p>
                  <button
                    type="button"
                    onClick={addSlab}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    + Add Slab
                  </button>
                </div>
                
                {formData.commission_slabs.length === 0 ? (
                  <div className="text-center py-4 text-slate-500 text-sm">
                    No slabs configured. Click "Add Slab" to create commission tiers.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.commission_slabs.map((slab, index) => (
                      <div key={index} className="flex items-center gap-2 bg-white p-3 rounded-lg border border-slate-200 flex-wrap">
                        <span className="text-sm text-slate-600">From</span>
                        <input
                          type="number"
                          min="0"
                          value={slab.min_referrals}
                          onChange={(e) => updateSlab(index, 'min_referrals', parseInt(e.target.value) || 0)}
                          className="input-base w-16 text-center"
                        />
                        <span className="text-sm text-slate-600">to</span>
                        <input
                          type="number"
                          min={slab.min_referrals}
                          value={slab.max_referrals ?? ''}
                          onChange={(e) => updateSlab(index, 'max_referrals', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="∞"
                          className="input-base w-16 text-center"
                        />
                        <span className="text-sm text-slate-600">enrollments =</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={slab.commission_percentage}
                          onChange={(e) => updateSlab(index, 'commission_percentage', parseFloat(e.target.value) || 0)}
                          className="input-base w-20 text-center"
                        />
                        <span className="text-sm text-slate-600">%</span>
                        <button
                          type="button"
                          onClick={() => removeSlab(index)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded ml-auto"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {formData.commission_slabs.length > 0 && (
                  <p className="text-xs text-slate-500 mt-3">
                    Leave "to" field empty for unlimited (e.g., "16+ enrollments")
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Partnership Manager */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Partnership Manager (Optional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.partnership_manager_name}
                  onChange={(e) => setFormData({ ...formData, partnership_manager_name: e.target.value })}
                  className="input-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.partnership_manager_phone}
                  onChange={(e) => setFormData({ ...formData, partnership_manager_phone: e.target.value })}
                  className="input-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.partnership_manager_email}
                  onChange={(e) => setFormData({ ...formData, partnership_manager_email: e.target.value })}
                  className="input-base"
                />
              </div>
            </div>
          </div>

          {/* Partnership Agreement */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Partnership Agreement
            </h4>
            <div className="bg-slate-50 rounded-lg p-4">
              {agreementUrl ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Agreement Uploaded</p>
                      <p className="text-xs text-slate-500">{agreementFile?.name || 'Partnership Agreement.pdf'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a 
                      href={agreementUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      View
                    </a>
                    <button
                      type="button"
                      onClick={() => { setAgreementUrl(''); setAgreementFile(null); }}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => handleAgreementUpload(e.target.files[0])}
                      className="hidden"
                      disabled={uploadingAgreement}
                    />
                    {uploadingAgreement ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-slate-600">Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        <span className="text-sm font-medium text-slate-700">Click to upload agreement</span>
                        <span className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX, JPG, PNG (max 10MB)</span>
                      </>
                    )}
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : (doctor ? 'Update Doctor' : 'Add Doctor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// QR Code Modal
const QRCodeModal = ({ data, onClose, onDownload }) => {
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_url);
      toast.success('Link copied!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Doctor QR Code</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 text-center">
          <div className="bg-white p-4 rounded-lg border border-slate-200 inline-block mb-4">
            <img 
              src={data.qr_code_base64} 
              alt="QR Code" 
              className="w-48 h-48"
            />
          </div>
          
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-500 mb-1">Referral Code</p>
              <p className="font-mono text-lg font-bold text-slate-900">{data.referral_code}</p>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-500 mb-1">Referral Link</p>
              <div className="flex items-center gap-2">
                <p className="text-sm text-indigo-600 truncate flex-1">{data.referral_url}</p>
                <button onClick={copyLink} className="p-1 hover:bg-slate-200 rounded">
                  <Copy className="w-4 h-4 text-slate-600" />
                </button>
                <a href={data.referral_url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-slate-200 rounded">
                  <ExternalLink className="w-4 h-4 text-slate-600" />
                </a>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={onClose} className="btn-secondary flex-1">
              Close
            </button>
            <button onClick={onDownload} className="btn-primary flex-1 flex items-center justify-center gap-2">
              <Download className="w-4 h-4" />
              Download QR
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorsPage;

// Doctor Image Upload Component (Drag & Drop)
const DoctorImageUpload = ({ currentUrl, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, or WebP images allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload/doctor-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onUpload(res.data.file_url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-lg p-4 transition-colors ${dragActive ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-slate-50'}`}
      data-testid="doctor-image-upload"
    >
      {currentUrl ? (
        <div className="flex items-center gap-4">
          <img src={currentUrl.startsWith('/api') ? `${API_URL}${currentUrl}` : currentUrl} alt="Doctor" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
          <div className="flex-1">
            <p className="text-sm text-slate-700 font-medium">Image uploaded</p>
            <p className="text-xs text-slate-400 truncate">{currentUrl}</p>
          </div>
          <label className="btn-secondary text-xs cursor-pointer">
            Replace
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
        </div>
      ) : (
        <label className="flex flex-col items-center gap-2 cursor-pointer py-4">
          {uploading ? (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-400" />
              <p className="text-sm text-slate-500">Drag & drop an image or <span className="text-indigo-600 font-medium">browse</span></p>
              <p className="text-xs text-slate-400">JPG, PNG or WebP. Max 5MB</p>
            </>
          )}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
};
